/**
 * EmployeeDetail.tsx — F12.M Etap D Part 2 (Employees fullscreen, Etap 1.3).
 *
 * Strona szczegółów Pracownika. Analog do `ContractorDetail.tsx` / `CompanyDetail.tsx`.
 * Refactor z drawera (`EmployeeDetailDrawer` w `Employees.tsx`) na fullscreen
 * z routem `/employees/:id` (edit) / `/employees/new` (create).
 *
 * Create=edit parity (mirror ContractorDetail — atomic-save refactor): usunięty
 * mechanizm save-and-continue (dawny F12.N D4: niejawny POST przy zmianie zakładki
 * w trybie create). Create i edit dzielą teraz JEDEN RecursiveFormRenderer — bez
 * aktywnego layoutu sam EmployeeDataTab (identycznie dla obu trybów). Wszystkie
 * zakładki formularza są dostępne od razu w create, przełączanie NIE wykonuje
 * żadnego zapytania HTTP. Tabele dodatkowe (additional_view/satellite_view) są
 * buforowane (bez HTTP, delty jadą w POST/PATCH — patrz saveAggregate). Zakładki
 * wymagające zapisanego rekordu (addresses, account, audit_log)
 * pozostają gated `!isCreate && id`. Pola dodatkowe NIE mają własnej zakładki:
 * pojedyncze pola układa w „Dane" silnik formatek.
 *
 * Tabs:
 *   - data — EmployeeDataTab (dynamic z layout_config employees + sekcja „Dane wrażliwe")
 *   - addresses — konfigurowalny blok `employee_addresses_view` (AddressesDynamicTab,
 *     polymorphic ownerType='employee') — dispatch przez `employeeContextRenderers`,
 *     NIE przez `tabRenderers` (kartoteka-blokify, mirror ContractorDetail)
 *   - account — konfigurowalny blok `employee_account_view` (EmployeeAccountTab, akcje
 *     na powiązanym koncie user'a) — dispatch przez `employeeContextRenderers`, NIE przez
 *     `tabRenderers` (kartoteka-blokify, mirror „Dostęp firm" ContractorDetail)
 *   - audit_log — generyczny blok `audit_log_view` (SystemModuleAuditTab) —
 *     dispatch przez `employeeContextRenderers`, NIE przez `tabRenderers`
 *     (kartoteka-blokify, reużywa generycznego bloku jak ContractorDetail)
 *
 * Save flow — JEDEN zapis (atomic-save refactor, backend saveAggregate):
 *   - create: JEDEN POST /employees (pola + extValues + additionalTables w tym
 *     samym body) → redirect do /employees/:newId.
 *   - edit: JEDEN PATCH /employees/:id (pola + extValues + additionalTables).
 *   Zero osobnego PUT ext-fields — backend zapisuje wszystko atomowo.
 *
 * PII (PESEL / numer dowodu) — orthogonal do atomic-save migracji, NIETKNIĘTE:
 *   view-mode pokazuje `peselMasked`/`idDocumentMasked` z GET response (gate
 *   `hr.employees.read_pesel` po stronie backendu); edit-mode input pusty =
 *   brak zmiany. Logika renderu żyje w `EmployeeDataTab`/`SensitiveSection`.
 *
 * Przycisk SQL (klocek układu `sql_button`, contextRenderer): zadanie w tle na pracowniku. Edycja — klik
 * najpierw zapisuje (`useSaveBeforeDecision`), podgląd — rusza od razu, nowy rekord — utworzenie →
 * zadanie → przejście (w oknie osadzonym zadanie rusza przed `onSaved`). Na czas zadania pasek blokady,
 * „Edytuj” / „Anonimizuj” / „Usuń” nieaktywne, a zapis, usunięcie i anonimizacja odrzucone 409
 * `RECORD_LOCKED_BY_RUN` dają czytelny komunikat (mirror ContractorDetail).
 *
 * Modale: AnonymizeEmployeeModal (RODO) + CreateAccountForExistingModal +
 *         ConfirmDialog (delete).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { AlertTriangle, ArrowLeft, Pencil, Save, Trash2, X as XIcon } from 'lucide-react';

import { AdditionalTableTab } from '@/components/additional/AdditionalTableTab';
import { AdditionalViewTab } from '@/components/additional/AdditionalViewTab';
import { AddressesDynamicTab } from '@/components/addresses/AddressesDynamicTab';
import { AttachmentsSection } from '@/components/attachments/AttachmentsSection';
import { buildDocumentPreviewContextRenderer } from '@/components/attachments/DocumentPreviewContextRenderer';
import { DocumentPreviewDockHost } from '@/components/attachments/DocumentPreviewDockHost';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AnonymizeEmployeeModal } from '@/components/employees/AnonymizeEmployeeModal';
import { CreateAccountForExistingModal } from '@/components/employees/CreateAccountForExistingModal';
import { EmployeeAccountTab } from '@/components/employees/EmployeeAccountTab';
import {
  buildEmployeeCoreRecordValues,
  EmployeeDataTab,
  type EmployeeFormData,
  EMPTY_EMPLOYEE_FORM,
} from '@/components/employees/EmployeeDataTab';
import {
  employeeEngineConfig,
  type EmployeeExtra,
  type EmployeeLookupOption,
} from '@/components/employees/employeeEngineConfig';
import {
  type ContextRenderers,
  RecursiveFormRenderer,
} from '@/components/form/RecursiveFormRenderer';
import { SaveFirstNotice } from '@/components/form/SaveFirstNotice';
import { IconButton } from '@/components/IconButton';
import { collectEngineFieldErrors } from '@/components/kartoteki/engine/engine-config';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { parseHiddenEmployeeAddressColumns } from '@/components/modules/layout-elements/EmployeeAddressesViewElement';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import {
  parseRecordLinksElementConfig,
  RecordLinksSection,
} from '@/components/record-links/RecordLinksSection';
import { RecordRunLockBanner } from '@/components/record-procedures/RecordRunLockBanner';
import {
  collectSqlButtonLabels,
  parseSqlButtonSettings,
} from '@/components/record-procedures/sql-button-settings';
import { SqlButton } from '@/components/record-procedures/SqlButton';
import { Spinner } from '@/components/Spinner';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import { useToast } from '@/contexts/ToastContext';
import { useAdditionalTableDeltas } from '@/hooks/useAdditionalTableDeltas';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import { applyCoreDefaults, useNewRecordDefaults } from '@/hooks/useNewRecordDefaults';
import { useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useRecordProcedureRuns } from '@/hooks/useRecordProcedureRuns';
import { useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import { useSystemModuleFields } from '@/lib/ext-list-columns';
import { parseSlugList } from '@/lib/kartoteki/formatka-order';
import { mapDictionarySaveError } from '@/lib/records/save-error-message';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useCompanies } from '@/lib/use-permissions';
import {
  buildIndentedOrgUnitOptions,
  EMPLOYEE_DETAIL_KEY,
  type EmployeeDetail as EmployeeDetailType,
  useEmployeesList,
  useOrgUnitsForCompany,
} from '@/lib/use-tenant-people';
import {
  buildFieldIssueMessages,
  buildValidationToastMessage,
  resolveFieldLabel,
} from '@/lib/validation-toast';

interface EmployeePayload {
  employee: EmployeeDetailType;
  // Prawa akcji per rekord (E3b) — liczone silnikiem z kategorią; front chowa Edytuj/Usuń.
  permissions?: { canEdit: boolean; canDelete: boolean };
}

/**
 * Tryb OSADZONY (okno wyboru pracownika z pola relacji — mirror `ContractorDetail`): id i tryb
 * przychodzą z propsów, nawigacja jest zastąpiona callbackami, zakładka nie żyje w URL-u
 * (URL należy do strony-gospodarza).
 */
export interface EmployeeDetailEmbeddedControls {
  mode: 'create' | 'edit';
  /** Wymagane w trybie edit — pracownik do edycji. */
  employeeId?: string;
  /** Firma-scope dla trybu create (prefill firmy); pomijane w edit. */
  companyId?: string;
  onSaved: (id: string) => void;
  onCancel: () => void;
}

export interface EmployeeDetailProps {
  embedded?: EmployeeDetailEmbeddedControls;
}

export function EmployeeDetail({ embedded }: EmployeeDetailProps = {}) {
  const { t } = useTranslation();
  const routeParams = useParams<{ id?: string }>();
  // Tryb osadzony bierze id/isCreate z propsów; tryb strony — z route'a (bez zmian).
  const id = embedded
    ? embedded.mode === 'edit'
      ? embedded.employeeId
      : undefined
    : routeParams.id;
  const isCreate = embedded ? embedded.mode === 'create' : !id || id === 'new';
  const navigate = useNavigate();
  // Powrót na listę niesie jej kontekst — patrz useWithMenuItem.
  const withMenuItem = useWithMenuItem();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fe = useFieldErrors();

  const [searchParams, setSearchParams] = useSearchParams();
  // F12.N E1.T5b: layout w nowym kształcie + role context dla filtrowania zakładek.
  const { layoutConfig, myRoleId, isSuperUser, previewDockEnabled } =
    useSystemModuleActiveLayout('employees');
  // Core invariant (bez tworzenia rekordu bez jawnie wybranej konkretnej firmy):
  // w trybie konkretnej firmy prefill companyId z aktywnego kontekstu; w trybie
  // "Wszystkie firmy" pole zostaje puste i user MUSI wybrać (CompanyPicker w
  // EmployeeDataTab), submit zablokowany w handleSave niżej.
  const companyScope = useActiveCompanyScope();

  const initialTab = embedded ? 'data' : (searchParams.get('tab') ?? 'data');
  const [tab, setTabRaw] = useState<string>(initialTab);

  // Sync URL → state (React Router 6 nie remountuje instancji na SPA navigate
  // /new → /:id — ten sam `element` w obu Routes, `useState` NIE re-runs).
  useEffect(() => {
    if (embedded) return;
    const urlTab = searchParams.get('tab') ?? 'data';
    if (urlTab !== tab) setTabRaw(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Create=edit parity fix (mirror ContractorDetail): zwykła zmiana zakładki,
  // bez żadnego niejawnego zapisu — w trybie create wszystkie zakładki formularza
  // są dostępne od razu, zapis następuje wyłącznie na klik „Zapisz" (saveMutation).
  function setTab(next: string) {
    setTabRaw(next);
    if (embedded) return;
    const params = new URLSearchParams(searchParams);
    if (next === 'data') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  // Tryb osadzony edit wchodzi w edycję dopiero po wczytaniu rekordu i sprawdzeniu prawa
  // (efekt niżej, przy `canEditRecord`).
  const [editing, setEditing] = useState(isCreate);
  const [form, setForm] = useState<EmployeeFormData>(EMPTY_EMPLOYEE_FORM);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showAnonymize, setShowAnonymize] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [peselMasked, setPeselMasked] = useState(false);
  const [idDocMasked, setIdDocMasked] = useState(false);

  const detailQ = useQuery({
    queryKey: id && !isCreate ? EMPLOYEE_DETAIL_KEY(id) : ['employee', 'new'],
    queryFn: async () => {
      const res = await api.get<EmployeePayload>(`/employees/${id}`);
      return res.data;
    },
    enabled: !isCreate,
  });

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) dla formularza nowego pracownika — także
  // w oknie osadzonym. Pola pokazujemy dopiero po ich wczytaniu (patrz `NewRecordDefaultsStatus`
  // niżej).
  const recordDefaults = useNewRecordDefaults('employees', isCreate);

  // Pola dodatkowe w zakładce „Dane": stan lokalny zasilany wartością z odpowiedzi GET rekordu
  // (`employee.extValues`, bez osobnego żądania). Pełny kontrakt synchronizacji (w tym ochrona
  // niezapisanych edycji przed odświeżeniem w tle) w nagłówku `useRecordExtValues.ts`. Przy
  // tworzeniu „wartością serwera" są wartości domyślne pól dodatkowych.
  const { extValues, handleExtChange, snapshotEdits, markSaved, discardEdits } = useRecordExtValues(
    {
      recordId: isCreate ? undefined : id,
      serverExtValues: isCreate ? recordDefaults.ext : detailQ.data?.employee.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );

  // Atomic-save refactor (mirror ContractorDetail): bufor delt tabel dodatkowych
  // (i szkiców add-row) we wspólnym hooku — kontrakt freshness `getBufferedValue`
  // w nagłówku `useAdditionalTableDeltas.ts`.
  const {
    handleAdditionalDeltasChange,
    getBufferedValue,
    flattenAdditionalDeltas,
    buildAdditionalParentValues,
    resetAdditionalDeltas,
    getAddRowDraft,
    handleAddRowDraftChange,
    resetAddRowDrafts,
  } = useAdditionalTableDeltas();

  // Lookups: firmy / działy aktualnej firmy / pracownicy aktualnej firmy — przeniesione
  // z EmployeeDataTab (silnik wymaga JEDNEGO kierunku przepływu: strona → runtime.extra →
  // renderer, mirror ContractorDetail's `companiesQ`/kanał NIP).
  const companiesQ = useCompanies();
  const orgUnitsQ = useOrgUnitsForCompany(form.companyId || null);
  const employeesQ = useEmployeesList({}); // wszystkie — filter w renderze po companyId

  const employeeLookups = useMemo(() => {
    const companies: EmployeeLookupOption[] = (companiesQ.data?.companies ?? []).map((c) => ({
      id: c.id,
      label: c.shortName || c.legalName,
    }));
    // Etykiety WCIĘTE wg głębokości drzewa (F24 Pakiet 0) — `parentId` tutaj niesie
    // `companyId` (filtr renderera), nie `org_units.parentId` (patrz komentarz w
    // `employeeEngineConfig.tsx` przy `orgUnitIdRenderer`).
    const orgUnits: EmployeeLookupOption[] = buildIndentedOrgUnitOptions(
      orgUnitsQ.data?.orgUnits ?? [],
    ).map((opt) => ({
      id: opt.value,
      label: opt.label,
      parentId: form.companyId || null,
    }));
    const managers: EmployeeLookupOption[] = (employeesQ.data?.employees ?? []).map((e) => ({
      id: e.id,
      label: `${e.lastName} ${e.firstName}`,
      parentId: e.companyId,
    }));
    return { companies, orgUnits, managers };
  }, [companiesQ.data, orgUnitsQ.data, employeesQ.data, form.companyId]);

  // Silnik formatek (keystone unifikacja-formatek, ostatni krok) — pola core pracownika
  // przez `employeeEngineConfig`. `hasError`/`extra` niosą to, co wcześniej trzymał
  // EmployeeDataTab lokalnie (podświetlenie walidacji + lookupy firmy/działu/przełożonego)
  // — silnik wymaga JEDNEGO kierunku przepływu (strona → runtime → renderer).
  const { renderField, fields: kartotekaFields } = useKartotekaRenderField<
    EmployeeFormData,
    EmployeeExtra
  >(employeeEngineConfig, {
    form,
    onChange: setForm,
    extValues,
    onExtChange: handleExtChange,
    editable: editing || isCreate,
    isCreate,
    hasError: (key) => !!fe.get(String(key)),
    getError: (key) => fe.get(String(key)),
    clearError: (key) => fe.clear(String(key)),
    extra: {
      ...employeeLookups,
      currentEmployeeId: id && !isCreate ? id : null,
    },
  });

  // Wartości formularza keyed po slugu (core + ext) — do oceny warunków widoczności
  // pól/bloków z kreatora. Przekazywane do EmployeeDataTab i RecursiveFormRenderer.
  const layoutFormData = useMemo<Record<string, unknown>>(
    () => ({ ...buildEmployeeCoreRecordValues(form), ...extValues }),
    [form, extValues],
  );

  // Wartości pól pracownika (core + dodatkowe) JAK NA EKRANIE, także niezapisane zmiany —
  // źródło wiązań `parent_field` słowników w wierszach tabel dodatkowych (zakładki dostają je
  // jako `parentValues`). Skalary/null. Stabilność referencji nie jest wymagana (klucz zapytania
  // o opcje to zserializowane wybrane wartości); `useMemo` tylko oszczędza przeliczanie mapy.
  const additionalOwnerValues = useMemo(
    () => buildAdditionalViewRecordValues(extValues, buildEmployeeCoreRecordValues(form)),
    [form, extValues],
  );

  // Prawa akcji per rekord (E3b) — serwer liczy je silnikiem z kategorią rekordu; front chowa
  // Edytuj/Usuń, gdy brak prawa. Brak danych (ładowanie) = pokazujemy (serwer i tak egzekwuje).
  const canEditRecord = detailQ.data?.permissions?.canEdit ?? true;
  const canDeleteRecord = detailQ.data?.permissions?.canDelete ?? true;

  // Przycisk SQL (klocek `sql_button`): zadania wykonywane w tle na tym pracowniku. Stan (aktywne zadanie
  // + ostatnie per procedura) odpytywany co ~2 s tylko przy aktywnym zadaniu; po jego końcu komunikat w
  // rogu i odświeżenie rekordu (zapytanie mogło go zmienić). Przy tworzeniu brak rekordu. Napisy
  // przycisków z układu służą komunikatom i paskowi blokady (zadanie zna tylko nazwę procedury).
  const sqlButtonLabels = useMemo(() => collectSqlButtonLabels(layoutConfig), [layoutConfig]);
  const runs = useRecordProcedureRuns('employee', isCreate ? undefined : id, {
    labels: sqlButtonLabels,
    onFinished: () => {
      if (id) void queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_KEY(id) });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      void queryClient.invalidateQueries({ queryKey: ['system-module-audit', 'employees', id] });
    },
  });
  // Przycisk kliknięty na NOWYM rekordzie: najpierw utworzenie (zwykły zapis), potem zadanie z id świeżo
  // utworzonego pracownika, dopiero potem przejście pod jego adres (albo oddanie id oknu osadzonemu) —
  // patrz `saveMutation.onSuccess`. `settle` kończy oczekiwanie przycisku (kręciołek), także przy porażce
  // zapisu; `label` to napis klikniętego przycisku (do komunikatu „Zadanie … zostało zlecone" w oknie).
  const pendingCreateRunRef = useRef<{
    procedureId: string;
    label: string;
    settle: () => void;
  } | null>(null);

  const employeeFieldsQ = useSystemModuleFields('employees');
  const employeeFieldLabel = (field: string) =>
    resolveFieldLabel(employeeFieldsQ.data ?? [], field);

  // Rekord, dla którego `form` jest aktualnie zhydrowany z serwera —
  // odróżnia "user edytuje TEN SAM rekord" (refetch spoza tego formularza NIE
  // MOŻE nadpisać niezapisanych zmian, w tym pól wrażliwych PESEL/dowód) od
  // zmiany rekordu (nawigacja na inny /employees/:id musi zhydratować mimo że
  // `editing` zostało `true` z poprzedniej sesji edycji — patrz guard niżej).
  // Zerowana przy wejściu w tryb create (efekt resetu niżej), żeby hydratacja
  // po create+redirect na nowy rekord przeszła normalnie. Pola dodatkowe
  // (`extValues`) mają własny, równoległy guard w `useRecordExtValues`.
  const hydratedRecordIdRef = useRef<string | undefined>(undefined);

  // Sync server → local form (TYLKO edit mode — reset trybu create ma osobny
  // efekt niżej, bez zależności od companyScope, żeby przełączenie firmy w
  // trakcie wypełniania formularza NIE kasowało wpisanych danych).
  useEffect(() => {
    if (isCreate) return;
    if (editing && hydratedRecordIdRef.current === id) return;
    if (detailQ.data?.employee) {
      const e = detailQ.data.employee;
      hydratedRecordIdRef.current = id;
      setForm({
        firstName: e.firstName,
        lastName: e.lastName,
        middleName: e.middleName,
        birthDate: e.birthDate,
        // PESEL/idDocumentNumber: w view-mode pokazujemy z odpowiedzi; po
        // wejściu w edit-mode w PERMcheckcie input start z pustym (user
        // wpisze nowe albo zostawi puste = no change). Zostawiamy snapshot.
        pesel: e.pesel,
        taxId: e.taxId,
        idDocumentType: e.idDocumentType,
        idDocumentNumber: e.idDocumentNumber,
        companyId: e.companyId,
        orgUnitId: e.orgUnitId,
        positionCode: e.positionCode,
        specialization: e.specialization,
        employmentType: e.employmentType,
        employmentStartDate: e.employmentStartDate,
        employmentEndDate: e.employmentEndDate,
        internalCode: e.internalCode,
        managerEmployeeId: e.managerEmployeeId,
        phone: e.phone,
        mobile: e.mobile,
        workEmail: e.workEmail,
        isActive: e.isActive,
        groups: e.groups ?? [],
        // Integracja Symfonia (read-only) — pozycja/status z FK, per firma. Hydratacja z detalu.
        symfoniaExternalId: e.symfoniaExternalId ?? null,
        symfoniaPosition: e.symfoniaPosition ?? null,
        symfoniaActive: e.symfoniaActive ?? null,
        categoryId: e.categoryId ?? null,
      });
      setPeselMasked(e.peselMasked);
      setIdDocMasked(e.idDocumentMasked);
    }
    // `editing` celowo NIE jest w deps — hydratacja ma reagować na faktyczną
    // zmianę `detailQ.data` (initial load / zmiana rekordu / refetch po
    // zapisie), NIE na sam moment przełączenia editing=false (co mogłoby na
    // chwilę pokazać jeszcze nieodświeżone dane sprzed refetcha wywołanego
    // przez saveMutation.onSuccess).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQ.data, isCreate, id]);

  // Tryb osadzony (edit): okno otwiera się po to, żeby poprawić rekord — wchodzimy w edycję
  // JEDNORAZOWO po wczytaniu rekordu i tylko gdy jest prawo edycji, a rekord nie jest usunięty
  // (bez prawa okno zostaje w podglądzie zamiast pokazywać „Zapisz", który serwer odrzuci).
  const embeddedEditArmedRef = useRef(false);
  useEffect(() => {
    if (embedded?.mode !== 'edit' || embeddedEditArmedRef.current || !detailQ.data) return;
    embeddedEditArmedRef.current = true;
    if (canEditRecord && detailQ.data.employee.gcrecord == null) setEditing(true);
  }, [embedded?.mode, detailQ.data, canEditRecord]);

  // Guard: czy companyId zostało już jednorazowo prefillowane (lub user wpisał
  // je sam) w BIEŻĄCEJ sesji create. Ref (nie state) — musi być czytelny „na
  // żywo" przez efekt niżej w TYM SAMYM przebiegu commitu, w którym efekt resetu
  // formularza (poniżej) go zeruje; state/`form.companyId` w deps dawałby wyścig
  // (stary — z poprzedniego trybu edit — companyId zablokowałby prefill przy
  // przejściu /employees/:id → /employees/new, bo router nie remontuje komponentu).
  const companyPrefilledRef = useRef(false);

  // Reset formularza — TYLKO przy wejściu w tryb create (mount lub przejście
  // edit→create bez remountu). Celowo BEZ companyScope w deps: późniejsza zmiana
  // aktywnej firmy w trakcie wypełniania formularza nie może skasować wpisanych
  // pól (mirror guard z CreateEmployeeModal w Employees.tsx). `recordDefaults.core` zmienia
  // referencję raz (wczytanie wartości domyślnych) — pola są wtedy jeszcze ukryte, więc nic
  // wpisanego nie ginie; wartości domyślne nadpisują wartości startowe z kodu.
  useEffect(() => {
    if (!isCreate) return;
    setForm(applyCoreDefaults(EMPTY_EMPLOYEE_FORM, recordDefaults.core));
    setPeselMasked(false);
    setIdDocMasked(false);
    companyPrefilledRef.current = false;
    hydratedRecordIdRef.current = undefined;
  }, [isCreate, recordDefaults.core]);

  // Prefill companyId z aktywnego kontekstu firmy — JEDNORAZOWO per sesja create
  // (guard `companyPrefilledRef`). Osobny efekt (nie w resetcie wyżej) — musi
  // móc odpalić się PÓŹNIEJ niż reset, bo `companyScope` potrafi rozwiązać się
  // asynchronicznie (czeka na /me) już PO pierwszym renderze strony. W trybie
  // "Wszystkie firmy" pole zostaje puste — user musi wybrać jawnie (CompanyPicker
  // w EmployeeDataTab), submit zablokowany dalej w handleSave.
  useEffect(() => {
    if (!isCreate) return;
    if (companyPrefilledRef.current) return;
    // Tryb osadzony: firma rekordu-gospodarza ma pierwszeństwo przed aktywnym kontekstem.
    const resolvedId = embedded?.companyId ?? companyScope.companyId;
    if (!embedded?.companyId && companyScope.mode !== 'company') return;
    if (!resolvedId) return;
    companyPrefilledRef.current = true;
    setForm((f) => (f.companyId ? f : { ...f, companyId: resolvedId }));
    // `recordDefaults.core`: po wczytaniu wartości domyślnych efekt resetu wyżej odbudowuje formularz
    // i zdejmuje `companyPrefilledRef` — firma (kontekst/okno osadzone) musi wejść ponownie.
  }, [
    isCreate,
    embedded?.companyId,
    companyScope.mode,
    companyScope.companyId,
    recordDefaults.core,
  ]);

  // Klik przycisku SQL, gdy formularz jest w edycji: najpierw ten sam zapis co „Zapisz”, zadanie dopiero po
  // jego sukcesie (useSaveBeforeDecision; „zapis już trwa" sprawdza sam hak po kluczu `saveMutationKey`
  // mutacji zapisu tej kopii formularza). `succeeded` / `aborted` są wołane z `saveMutation.onSuccess/onError`.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing,
    recordId: isCreate ? undefined : id,
    save: handleSave,
  });

  const saveMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (data: EmployeeFormData) => {
      // Backend wymaga optional fields → null/undefined dla pustych
      const basePayload: Record<string, unknown> = {
        firstName: data.firstName,
        lastName: data.lastName,
      };
      // companyId tylko gdy wpisany — pusty string ('') by Zod `.uuid()` odrzucił
      // przed fallbackiem do X-Company-Id headera (F12.N D5).
      if (data.companyId) basePayload.companyId = data.companyId;
      const optional: Array<[keyof EmployeeFormData, string]> = [
        ['middleName', 'middleName'],
        ['birthDate', 'birthDate'],
        ['taxId', 'taxId'],
        ['idDocumentType', 'idDocumentType'],
        ['orgUnitId', 'orgUnitId'],
        ['positionCode', 'positionCode'],
        ['specialization', 'specialization'],
        ['employmentType', 'employmentType'],
        ['employmentStartDate', 'employmentStartDate'],
        ['employmentEndDate', 'employmentEndDate'],
        ['internalCode', 'internalCode'],
        ['managerEmployeeId', 'managerEmployeeId'],
        ['phone', 'phone'],
        ['mobile', 'mobile'],
        ['workEmail', 'workEmail'],
      ];
      for (const [key, payloadKey] of optional) {
        const v = data[key];
        if (v !== null && v !== undefined && v !== '') basePayload[payloadKey] = v;
      }
      basePayload.isActive = data.isActive;
      // Grupy — wysyłamy ZAWSZE (także pustą tablicę), inaczej odznaczenie wszystkich grup
      // nie miałoby jak dojść do backendu (`undefined` = brak zmiany).
      basePayload.groups = data.groups;
      // Kategoria — wysyłamy ZAWSZE (także null), inaczej wyczyszczenie kategorii
      // nie miałoby jak dojść do backendu (`undefined` = brak zmiany na PATCH).
      basePayload.categoryId = data.categoryId;
      if (data.pesel) basePayload.pesel = data.pesel;
      if (data.idDocumentNumber) basePayload.idDocumentNumber = data.idDocumentNumber;

      // Atomic-save refactor (mirror ContractorDetail): jeden zapis — extValues +
      // delty tabel dodatkowych jadą w TYM SAMYM body co pola pracownika (backend
      // zapisuje wszystko w jednej transakcji, patrz saveAggregate).
      // Migawka edycji tuż przed wysłaniem: edycja zrobiona w trakcie żądania nie zostanie zdjęta
      // przez `markSaved()` w onSuccess (patrz nagłówek `useRecordExtValues.ts`).
      snapshotEdits();
      if (Object.keys(extValues).length > 0) {
        basePayload.extValues = extValues;
      }
      const additionalTablesBody = flattenAdditionalDeltas();
      if (additionalTablesBody) {
        basePayload.additionalTables = additionalTablesBody;
        // Wartości pól pracownika z ekranu dla wiązań `parent_field` w wierszach tabel
        // dodatkowych — OBOK delt (nie w nich), tylko slugi użyte przez wiązania, tylko skalary.
        const additionalParentValues = await buildAdditionalParentValues(
          buildAdditionalViewRecordValues(extValues, buildEmployeeCoreRecordValues(data)),
          'default',
        );
        if (additionalParentValues) {
          basePayload.additionalParentValues = additionalParentValues;
        }
      }

      let recordId: string;
      if (isCreate) {
        const res = await api.post<{ employee: { id: string } }>('/employees', basePayload);
        recordId = res.data.employee.id;
      } else {
        await api.patch(`/employees/${id}`, basePayload);
        recordId = id!;
      }
      return { employee: { id: recordId } };
    },
    onSuccess: async (data) => {
      // Udany zapis gasi podświetlenia z poprzedniej nieudanej próby (ramka + tekst).
      fe.clearAll();
      // Pola dodatkowe zapisane razem z pracownikiem — od tej chwili wartość serwera (z odświeżonego
      // rekordu) może je nadpisać; bez tego guard niezapisanych edycji trzymałby stare wartości.
      markSaved();
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      // Atomic-save refactor: backend już zapisał tabele dodatkowe atomowo —
      // odśwież rekordy (prefix match łapie wszystkie
      // ['additional-records', tableId, parentId]) i wyczyść lokalny bufor delt.
      queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      resetAdditionalDeltas();
      // Finding [G] (pre-commit-verify): szkic niezatwierdzonego add-row też czyścimy
      // po udanym zapisie — spójnie z buforem zatwierdzonych wierszy powyżej.
      resetAddRowDrafts();

      if (isCreate && data?.employee?.id) {
        toast.show({ variant: 'success', title: t('tenant.employees.toast.created') });
        // Przycisk SQL kliknięty na nowym rekordzie: zadanie rusza TERAZ — z id nowego pracownika, PRZED
        // przejściem pod jego adres (albo oddaniem id oknu osadzonemu, które zwykle się zamyka). Porażka
        // zlecenia dostaje własny komunikat; rekord już istnieje, więc przejście i tak następuje.
        if (pendingCreateRunRef.current !== null) {
          await runPendingCreateRun(data.employee.id);
        }
        // Tryb osadzony: nie nawigujemy — id nowego rekordu dostaje okno-gospodarz.
        if (embedded) {
          embedded.onSaved(data.employee.id);
          return;
        }
        // Karta „nowy" ma zamienić się w kartę zapisanego rekordu (replaceTab). Tu, w
        // odróżnieniu od pozostałych kartotek, strona REMONTUJE się przez EmployeeDetailKeyed
        // (`key={id}`), więc świeży montaż i tak zeruje `editing` — poniższe jest defensywne
        // (nieszkodliwe, gdyby `key` kiedyś zniknął).
        setEditing(false);
        navigate(withMenuItem(`/employees/${data.employee.id}`), { state: { replaceTab: true } });
        return;
      }
      toast.show({ variant: 'success', title: t('tenant.employees.toast.updated') });
      if (id) {
        queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_KEY(id) });
      }
      // `setEditing(false)` i `succeeded()` razem, bez `await` między nimi (wymóg haka). Zadanie przycisku
      // SQL działa na danych już zapisanych w bazie, więc nie czeka na odświeżenie rekordu w formularzu.
      setEditing(false);
      // Tryb osadzony (edit): powiadom okno-gospodarza; zamknięcie okna zostaje w gestii usera.
      if (embedded && id) embedded.onSaved(id);
      // Zapis wywołany kliknięciem przycisku SQL → teraz startuje zadanie (no-op, gdy zapis poszedł
      // zwykłym „Zapisz"). Tylko gałąź edycji — przy tworzeniu zadanie rusza w gałęzi wyżej.
      saveBeforeDecision.succeeded();
    },
    onError: (err) => {
      // Zapis wywołany kliknięciem przycisku SQL: każdy błąd kończy sprawę — zadanie się nie uruchomi
      // (w edycji bramka zapomina czekające zlecenie, na nowym rekordzie zapamiętane zadanie jest zdejmowane).
      saveBeforeDecision.aborted();
      settlePendingCreateRun();
      // Kolejność komunikatu: (1) polski z issue Zoda, (2) własny komunikat serwera
      // z `details` (reguły krzyżowe / refine, bez `issues`), (3) generyk.
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      const code = extractErrorCode(err);
      if (code === 'VALIDATION_ERROR') {
        const specific = buildValidationToastMessage(err, employeeFieldLabel, t);
        toast.show({
          variant: 'error',
          title: specific ?? t('tenant.employees.toast.validationError'),
        });
        return;
      }
      // Odrzucona część słownikowa zapisu (pozycja spoza słownika / brak lub zła wartość
      // parametru słownika) — czytelna przyczyna zamiast ogólnego „błąd zapisu".
      const dictionaryMsg = mapDictionarySaveError(err, t);
      if (dictionaryMsg !== null) {
        toast.show({ variant: 'error', title: dictionaryMsg });
        return;
      }
      // Zapis odrzucony, bo na rekordzie trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania
      // zamiast ogólnego „konflikt”; odświeżony stan zadań pokaże pasek blokady.
      if (runs.handleLockedError(err)) return;
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.show({ variant: 'error', title: t('tenant.employees.toast.conflict') });
        return;
      }
      // F24 Pakiet 0 — nadpisanie przełożonego / jednostka organizacyjna. Podświetlenie
      // pola (mirror `fe.applyServerError` VALIDATION_ERROR) + toast z konkretną przyczyną.
      if (code === 'MANAGER_NOT_IN_COMPANY') {
        fe.set('managerEmployeeId', t('tenant.employees.toast.errors.MANAGER_NOT_IN_COMPANY'));
        toast.show({
          variant: 'error',
          title: t('tenant.employees.toast.errors.MANAGER_NOT_IN_COMPANY'),
        });
        return;
      }
      if (code === 'MANAGER_SELF_REFERENCE') {
        fe.set('managerEmployeeId', t('tenant.employees.toast.errors.MANAGER_SELF_REFERENCE'));
        toast.show({
          variant: 'error',
          title: t('tenant.employees.toast.errors.MANAGER_SELF_REFERENCE'),
        });
        return;
      }
      if (code === 'ORG_UNIT_NOT_FOUND') {
        fe.set('orgUnitId', t('tenant.employees.toast.errors.ORG_UNIT_NOT_FOUND'));
        toast.show({
          variant: 'error',
          title: t('tenant.employees.toast.errors.ORG_UNIT_NOT_FOUND'),
        });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.employees.toast.saveError') });
    },
  });

  // Core invariant — bez tworzenia rekordu bez jawnie wybranej konkretnej firmy:
  // blokuje submit w trybie create dopóki companyId nie jest ustawiony (prefilled
  // automatycznie w trybie konkretnej firmy, wymaga jawnego pick w trybie
  // "Wszystkie firmy" — patrz useEffect wyżej + CompanyPicker w employeeEngineConfig).
  // Zwraca, czy zapis wystartował (false = zatrzymała go walidacja w panelu) — bramka przycisku SQL i
  // `useSaveBeforeDecision` czekają tylko na zapis, który naprawdę ruszył.
  function handleSave(): boolean {
    if (isCreate && !form.companyId) {
      toast.show({ variant: 'error', title: t('common.companyRequired') });
      return false;
    }

    // Format wg TYPU pola (wspólna oś silnika) dla pól core i dodatkowych. PESEL/dowód
    // są poza silnikiem (PII, poza `system_module_fields`) — ich nie tyka.
    const fieldErrors = collectEngineFieldErrors(
      employeeEngineConfig,
      form,
      kartotekaFields,
      extValues,
      t,
    );
    if (Object.keys(fieldErrors).length > 0) {
      fe.clearAll();
      for (const [key, msg] of Object.entries(fieldErrors)) {
        fe.set(key, msg);
      }
      toast.show({ variant: 'error', title: t('common.validationIssue.formToast') });
      return false;
    }

    saveMutation.mutate(form);
    return true;
  }

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/employees/${id}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.employees.toast.deleted') });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      // Tryb osadzony: zamknij okno zamiast nawigować na listę.
      if (embedded) {
        embedded.onCancel();
        return;
      }
      navigate(withMenuItem('/employees'));
    },
    onError: (err) => {
      // Usuwanie odrzucone na czas zadania przycisku SQL — jak przy zapisie (opis zadania + pasek blokady).
      if (runs.handleLockedError(err)) return;
      toast.show({ variant: 'error', title: t('tenant.employees.toast.deleteError') });
    },
  });

  const anonymizeMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/employees/${id}/anonymize`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.employees.toast.anonymized') });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      if (id) queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_KEY(id) });
      setShowAnonymize(false);
    },
    onError: (err) => {
      // Anonimizacja odrzucona na czas zadania przycisku SQL — opis zadania + pasek blokady.
      if (runs.handleLockedError(err)) return;
      if (isAxiosError(err) && err.response?.data?.error === 'PERMISSION_DENIED') {
        toast.show({
          variant: 'error',
          title: t('tenant.employees.toast.errors.PERMISSION_DENIED', {
            defaultValue: t('tenant.employees.toast.anonymizeError'),
          }),
        });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.employees.toast.anonymizeError') });
    },
  });

  /** Kończy oczekiwanie przycisku SQL klikniętego na nowym rekordzie bez uruchamiania zadania. */
  function settlePendingCreateRun(): void {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    pending?.settle();
  }

  /**
   * Zleca zadanie zapamiętane przy kliknięciu na nowym rekordzie — z id świeżo utworzonego pracownika. Firma
   * zlecenia nie wymaga własnego nagłówka: prawo edycji serwer liczy z firmy samego rekordu. W oknie
   * osadzonym (zamyka się zaraz po utworzeniu, więc nie pokaże stanu zadania) przyjęcie zlecenia ogłasza
   * komunikat.
   */
  async function runPendingCreateRun(newEmployeeId: string): Promise<void> {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    if (pending === null) return;
    try {
      const run = await runs.startRun(pending.procedureId, newEmployeeId);
      if (run !== null && embedded) {
        toast.show({
          variant: 'info',
          title: t('recordProcedureRuns.toast.started', { label: pending.label }),
        });
      }
    } finally {
      pending.settle();
    }
  }

  /**
   * Start zadania przycisku SQL. W edycji i podglądzie wołany PO bramce zapisu (edycja: najpierw zwykły
   * zapis, błąd zapisu = zadanie nie startuje; podgląd: od razu). Na nowym rekordzie zapamiętuje
   * zadanie i uruchamia zwykły zapis (utworzenie) — resztę robi `saveMutation.onSuccess`.
   */
  function handleSqlButtonStart(procedureId: string, label: string): Promise<unknown> {
    if (!isCreate) return runs.startRun(procedureId);
    // Utworzenie już trwa (kliknięte „Zapisz” albo wcześniejszy przycisk) — drugi POST utworzyłby pracownika
    // drugi raz. Twardy bezpiecznik: stan mutacji czytany na żywo (`useIsMutating` odświeża się dopiero po
    // renderze, a kliknięcie może wyprzedzić render).
    if (
      queryClient.isMutating({ mutationKey: saveBeforeDecision.saveMutationKey, exact: true }) > 0
    ) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      pendingCreateRunRef.current = { procedureId, label, settle: resolve };
      try {
        if (!handleSave()) settlePendingCreateRun();
      } catch (err) {
        // Wyjątek zanim zapis ruszył: zapamiętane zadanie nie może zostać w refie (wystartowałoby po
        // następnym, zwykłym zapisie) — czyścimy je i odrzucamy obietnicę (przycisk wraca do gotowości).
        pendingCreateRunRef.current = null;
        reject(err);
      }
    });
  }

  function handleCancel() {
    // Anulowanie gasi podświetlenia z nieudanej próby zapisu (ramka + tekst pod polem).
    fe.clearAll();
    // Tryb osadzony: „Anuluj" zamyka okno (create i edit) — mirror ContractorDetail.
    if (embedded) {
      embedded.onCancel();
      return;
    }
    if (isCreate) {
      navigate(withMenuItem('/employees'));
      return;
    }
    setEditing(false);
    discardEdits();
    // Anuluj porzuca też zbuforowane zmiany tabel dodatkowych i szkice add-row (nie tylko zapis).
    resetAdditionalDeltas();
    resetAddRowDrafts();
    // Resetuj form do server state. Bezpieczne mimo że to też setForm z
    // detailQ.data: to jawna akcja usera (klik Cofnij), nie efekt reagujący
    // na tło query — nie może więc cicho nadpisać trwającej edycji (nie jest
    // instancją bugu z efektu hydratacji wyżej, wręcz jego zamierzonym
    // przeciwieństwem: user PROSI o odrzucenie zmian).
    if (detailQ.data?.employee) {
      const e = detailQ.data.employee;
      setForm((prev) => ({
        ...prev,
        firstName: e.firstName,
        lastName: e.lastName,
        middleName: e.middleName,
        birthDate: e.birthDate,
        pesel: e.pesel,
        taxId: e.taxId,
        idDocumentType: e.idDocumentType,
        idDocumentNumber: e.idDocumentNumber,
        companyId: e.companyId,
        orgUnitId: e.orgUnitId,
        positionCode: e.positionCode,
        specialization: e.specialization,
        employmentType: e.employmentType,
        employmentStartDate: e.employmentStartDate,
        employmentEndDate: e.employmentEndDate,
        internalCode: e.internalCode,
        managerEmployeeId: e.managerEmployeeId,
        phone: e.phone,
        mobile: e.mobile,
        workEmail: e.workEmail,
        isActive: e.isActive,
        groups: e.groups ?? [],
        symfoniaExternalId: e.symfoniaExternalId ?? null,
        symfoniaPosition: e.symfoniaPosition ?? null,
        symfoniaActive: e.symfoniaActive ?? null,
        categoryId: e.categoryId ?? null,
      }));
    }
  }

  if (!isCreate && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="employee-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <div
        className="m-6 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        data-testid="employee-detail-error"
      >
        {t('tenant.employees.detail.loadError')}
      </div>
    );
  }

  // Nowy pracownik: pola dopiero po wczytaniu wartości domyślnych (albo komunikat z „Spróbuj
  // ponownie”). Po wszystkich hookach.
  if (isCreate && recordDefaults.status !== 'ready') {
    return <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="employee-detail" />;
  }

  const employee = detailQ.data?.employee;
  const isDeleted = employee?.gcrecord != null;
  // Rekord zablokowany na czas zadania przycisku SQL (decyzja „A”): aktywne zadanie albo właśnie
  // wysyłane zlecenie — „Edytuj”, „Anonimizuj” i „Usuń” nieaktywne (serwer odrzuca te operacje tak czy inaczej).
  const recordLocked = runs.active !== null || runs.starting;
  const fullName = isCreate
    ? t('tenant.employees.detail.newTitle')
    : `${form.lastName} ${form.firstName}${form.middleName ? ' ' + form.middleName : ''}`;

  // Mapa rendererów elementów zależnych od kontekstu — wspólna dla silnika (zwykłe
  // zakładki) i dedykowanej zakładki „Dane" (EmployeeDataTab), żeby element „widokowy"
  // (powiązania/załączniki/…) postawiony w „Dane" renderował się żywcem, nie jako
  // martwy placeholder. Do czasu przejścia kartoteki w całości na silnik.
  const employeeContextRenderers: ContextRenderers = {
    // Atomic-save refactor (mirror ContractorDetail): tabele dodatkowe
    // buforowane — dostępne już w trybie create (bez HTTP), delty jadą w
    // POST/PATCH razem z resztą body. `getBufferedValue` czyta AKTUALNĄ
    // wartość z bufora hooka przy każdym renderze, więc remount panelu
    // (np. Tabs przełączony i z powrotem) nie zeruje bufora.
    // Tabela dodatkowa zajmuje całą szerokość sekcji (`col-span-full`) — tak samo jak w
    // podglądzie kreatora formatki; bez tego trafiłaby w jedną komórkę dwukolumnowej siatki.
    additional_view: ({ element }) => {
      const tableSlug =
        typeof element?.settings?.tableSlug === 'string' ? element.settings.tableSlug : '';
      return tableSlug ? (
        <div className="col-span-full">
          <AdditionalTableTab
            tableSlug={tableSlug}
            parentId={id ?? null}
            editable={editing || isCreate}
            buffered
            onDeltasChange={handleAdditionalDeltasChange}
            getBufferedValue={getBufferedValue}
            getAddRowDraft={getAddRowDraft}
            onAddRowDraftChange={handleAddRowDraftChange}
            parentValues={additionalOwnerValues}
            parentKey="default"
            columnSettings={element.settings}
          />
        </div>
      ) : null;
    },
    satellite_view: ({ element }) => {
      const tableSlug =
        typeof element?.settings?.tableSlug === 'string' ? element.settings.tableSlug : '';
      return tableSlug ? (
        <div className="col-span-full">
          <AdditionalTableTab
            tableSlug={tableSlug}
            parentId={id ?? null}
            editable={editing || isCreate}
            buffered
            onDeltasChange={handleAdditionalDeltasChange}
            getBufferedValue={getBufferedValue}
            getAddRowDraft={getAddRowDraft}
            onAddRowDraftChange={handleAddRowDraftChange}
            parentValues={additionalOwnerValues}
            parentKey="default"
            columnSettings={element.settings}
          />
        </div>
      ) : null;
    },
    // F22 (widoki dodatkowe) — sekcja read-only zasilana SQL. Pracownik nie jest klasą
    // dokumentu, wiring lokalny — mirror struktury `additional_view`/`satellite_view` powyżej.
    // recordValues: ext-fields (`extValues`, żywe, działa w create) SCALONE z polami core
    // (`buildEmployeeCoreRecordValues(form)`, słownik slug→stateKey w `EmployeeDataTab.tsx`) —
    // wiązania `record_field` działają zarówno na pola dodatkowe, jak i na pola core pracownika.
    // `context.company`/`context.user`/`constant` działają bez zmian.
    additional_query_view: ({ element }) => {
      const viewSlug =
        typeof element?.settings?.viewSlug === 'string' ? element.settings.viewSlug : '';
      return viewSlug ? (
        <div className="col-span-full">
          <AdditionalViewTab
            viewSlug={viewSlug}
            recordId={id ?? null}
            recordValues={additionalOwnerValues}
            columnSettings={element.settings}
          />
        </div>
      ) : null;
    },
    // Fala B: tabela załączników kartoteki pracownika — wymaga zapisanego
    // rekordu (ownerId = employee.id). companyId z aktualnego stanu formularza
    // (ten sam widget w edit i po zapisie w create → redirect).
    attachments_view: () =>
      id ? (
        <AttachmentsSection
          ownerType="employee"
          ownerId={id}
          companyId={form.companyId || undefined}
        />
      ) : null,
    ...buildDocumentPreviewContextRenderer({
      ownerType: 'employee',
      ownerId: id,
      companyId: form.companyId || undefined,
      extValues,
    }),
    // F.RL (powiązania między kartotekami) — blok self-persisting, niezależny
    // od atomic-save formularza. Wymaga zapisanego rekordu (brak w trybie create).
    // `element.settings` — per-placement config ustawiony w builderze, parsowany defensywnie.
    record_links_view: ({ element }) =>
      id ? (
        <RecordLinksSection
          ownerType="employee"
          ownerId={id}
          companyId={companyScope.companyId}
          config={parseRecordLinksElementConfig(element.settings)}
        />
      ) : null,
    // Konfigurowalne zakładki — „Adresy pracownika" jako przenośny blok (mirror
    // ContractorDetail). `settings.hiddenColumnIds` (ikonka oka w formatce bloku)
    // filtruje kolumny listy adresów.
    employee_addresses_view: ({ element }) =>
      id ? (
        <AddressesDynamicTab
          ownerType="employee"
          ownerId={id}
          testIdPrefix="employee-addresses"
          hiddenColumnIds={parseHiddenEmployeeAddressColumns(element.settings)}
          columnOrder={parseSlugList(element.settings, 'columnOrder')}
        />
      ) : (
        <SaveFirstNotice message={t('tenant.employees.detail.tabDisabled.save')} />
      ),
    // Kanoniczny element subcollection silnika (mirror ContractorDetail): layouty
    // wciąż w starym kształcie (przed migracją E1.T7) lub zbudowane przez builder
    // emitują generyczny `related_records_table` — dispatch po `collection` na
    // ten sam komponent, co nazwany blok powyżej (bez hiddenColumnIds — ustawienie
    // bloku nie istnieje w tym generycznym kształcie).
    related_records_table: ({ element }) => {
      if (!id) {
        return <SaveFirstNotice message={t('tenant.employees.detail.tabDisabled.save')} />;
      }
      const collection =
        typeof element.settings?.collection === 'string' ? element.settings.collection : '';
      switch (collection) {
        case 'addresses':
          return (
            <AddressesDynamicTab
              ownerType="employee"
              ownerId={id}
              testIdPrefix="employee-addresses"
            />
          );
        default:
          return null;
      }
    },
    // Konfigurowalne zakładki — „Historia zmian" jako przenośny element PER KARTOTEKA
    // (`employee_audit_view`). Stary wspólny `audit_log_view` zostaje jako fallback.
    employee_audit_view: () =>
      id ? (
        <SystemModuleAuditTab moduleSlug="employees" recordId={id} testIdPrefix="employee-audit" />
      ) : (
        <SaveFirstNotice message={t('tenant.employees.detail.tabDisabled.save')} />
      ),
    audit_log_view: () =>
      id ? (
        <SystemModuleAuditTab moduleSlug="employees" recordId={id} testIdPrefix="employee-audit" />
      ) : (
        <SaveFirstNotice message={t('tenant.employees.detail.tabDisabled.save')} />
      ),
    // Konfigurowalne zakładki — „Konto pracownika" jako przenośny blok (mirror
    // „Dostęp firm" kontrahentów). Panel akcji na powiązanym koncie user'a (nie tabela),
    // wymaga zapisanego rekordu (id).
    employee_account_view: () =>
      id ? (
        <EmployeeAccountTab
          employeeId={id}
          account={employee?.account ?? null}
          isDeleted={isDeleted}
          onOpenCreateAccount={() => setShowCreateAccount(true)}
        />
      ) : (
        <SaveFirstNotice message={t('tenant.employees.detail.tabDisabled.save')} />
      ),
    // Klocek „Przycisk SQL”: zadanie w tle na tym pracowniku. W edycji klik najpierw zapisuje rekord
    // (bramka `useSaveBeforeDecision`; błąd zapisu = zadanie nie rusza), w podglądzie rusza od razu, na
    // nowym rekordzie — utworzenie, zadanie, przejście (w oknie osadzonym: zadanie przed `onSaved(id)`).
    // Prawo: ten sam sygnał co „Edytuj” (serwer i tak sprawdza); pracownik usunięty ma „Edytuj”
    // ukryte, a serwer odrzuca na nim zapis, więc przycisk też jest wyszarzony. „Zapisz" w toku też
    // blokuje przycisk — drugi klik nie może wysłać drugiego zapisu. Identyfikatory testowe per klocek
    // (`sql-button-<id klocka>`): strona może mieć kilka przycisków. Klocek bez procedury / napisu: nic.
    sql_button: ({ element }) => {
      const cfg = parseSqlButtonSettings(element.settings);
      if (cfg === null) return null;
      return (
        <SqlButton
          label={cfg.label}
          procedureId={cfg.procedureId}
          active={runs.active}
          lastRun={runs.lastByProcedure.find((run) => run.procedureId === cfg.procedureId)}
          canEdit={canEditRecord && !isDeleted}
          starting={runs.starting || saveMutation.isPending}
          beforeStart={isCreate ? undefined : saveBeforeDecision.beforeDecision}
          onStart={(procedureId) => handleSqlButtonStart(procedureId, cfg.label)}
          testIdPrefix={`sql-button-${element.id}`}
        />
      );
    },
  };

  return (
    <DocumentPreviewDockHost
      enabled={previewDockEnabled && !!id}
      moduleId="employees"
      ownerType="employee"
      ownerId={id}
      companyId={form.companyId || undefined}
    >
      <div className="space-y-4 p-6" data-testid="employee-detail-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {/* Strzałka wstecz — tylko na stronie; w oknie zamyka je przycisk Modala. */}
            {!embedded && (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={t('common.back')}
                data-testid="employee-detail-back"
                onClick={() => navigate(withMenuItem('/employees'))}
              >
                <ArrowLeft />
              </IconButton>
            )}
            <div>
              {/* W oknie tytuł „Nowy pracownik" ma już rama Modala — bez dubla (mirror kontrahenta). */}
              {isCreate ? (
                embedded ? null : (
                  <h1
                    className="text-2xl font-semibold text-text-primary"
                    data-testid="employee-detail-create-title"
                  >
                    {t('tenant.employees.detail.newTitle')}
                  </h1>
                )
              ) : (
                <>
                  <h1 className="text-2xl font-semibold text-text-primary">{fullName}</h1>
                  <div className="mt-1 flex items-center gap-2">
                    {!employee?.isActive && (
                      <Badge color="gray">{t('tenant.employees.badge.inactive')}</Badge>
                    )}
                    {isDeleted && <Badge color="red">{t('tenant.employees.badge.deleted')}</Badge>}
                    {employee?.account && (
                      <Badge color={employee.account.status === 'active' ? 'green' : 'gray'}>
                        {t('tenant.employees.badge.hasAccount')}
                      </Badge>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Akcje */}
          <div className="flex items-center gap-2">
            {!editing && !isCreate && !isDeleted && (
              <>
                {canEditRecord && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Pencil size={14} aria-hidden="true" />}
                    onClick={() => setEditing(true)}
                    disabled={recordLocked}
                    data-testid="employee-detail-edit-btn"
                  >
                    {t('common.edit')}
                  </Button>
                )}
                {/* Anonimizacja (nieodwracalna, RODO) i usuwanie — tylko na pełnej karcie, nie
                    w oknie wyboru rekordu (tam się wybiera i poprawia, nie kasuje). */}
                {!embedded && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<AlertTriangle size={14} aria-hidden="true" />}
                    onClick={() => setShowAnonymize(true)}
                    disabled={recordLocked}
                    data-testid="employee-detail-anonymize-btn"
                  >
                    {t('tenant.employees.actions.anonymize')}
                  </Button>
                )}
                {canDeleteRecord && !embedded && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Trash2 size={14} aria-hidden="true" />}
                    onClick={() => setConfirmDelete(true)}
                    disabled={recordLocked}
                    data-testid="employee-detail-delete-btn"
                  >
                    {t('common.delete')}
                  </Button>
                )}
              </>
            )}
            {(editing || isCreate) && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Save size={14} aria-hidden="true" />}
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  data-testid="employee-detail-save-btn"
                >
                  {t('common.save')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<XIcon size={14} aria-hidden="true" />}
                  onClick={handleCancel}
                  disabled={saveMutation.isPending}
                  data-testid="employee-detail-cancel-btn"
                >
                  {t('common.cancel')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Zadanie przycisku SQL na rekordzie (w kolejce lub w toku): rekordu nie da się zapisać, zanonimizować
          ani usunąć, „Edytuj”, „Anonimizuj” i „Usuń” są nieaktywne; pasek znika po zakończeniu zadania. */}
        {runs.active ? (
          <RecordRunLockBanner
            run={runs.active}
            label={runs.labelFor(runs.active)}
            refreshFailed={runs.isError}
          />
        ) : null}

        {/* Create=edit parity fix (mirror ContractorDetail): create i edit współdzielą
          teraz RecursiveFormRenderer. Bez aktywnego layoutu (`!layoutConfig`) — sam
          EmployeeDataTab (identycznie dla obu trybów). Z layoutem — RecursiveFormRenderer
          rendruje top-bar zakładek (z filtrem per-rola, sortem, hidden) + delegacja
          contentu zakładek do tabRenderers. Slugi w mapie tabRenderers to seed-slugi
          z `seed-system-modules.ts`. Zakładki wymagające zapisanego rekordu (addresses,
          account, audit_log) zostają gated `!id` — w trybie create widoczne
          w tab-barze, ale bez treści. Tabele dodatkowe (additional_view/satellite_view)
          są WYJĄTKIEM — buforowane, dostępne od razu bez HTTP, delty jadą w POST/PATCH
          razem z resztą body. */}
        {!layoutConfig ? (
          <EmployeeDataTab
            form={form}
            onChange={setForm}
            editable={editing || isCreate}
            isCreate={isCreate}
            peselMasked={peselMasked}
            idDocumentMasked={idDocMasked}
            contextRenderers={employeeContextRenderers}
            renderField={renderField}
            invalid={(field) => !!fe.get(field)}
            error={(field) => fe.get(field)}
            formData={layoutFormData}
            effectiveSupervisor={employee?.effectiveSupervisor}
          />
        ) : (
          <RecursiveFormRenderer
            layoutConfig={layoutConfig}
            activeTabId={tab}
            onTabChange={setTab}
            myRoleId={myRoleId}
            isSuperUser={isSuperUser}
            renderField={renderField}
            formData={layoutFormData}
            tabRenderers={{
              data: () => (
                <EmployeeDataTab
                  form={form}
                  onChange={setForm}
                  editable={editing || isCreate}
                  isCreate={isCreate}
                  peselMasked={peselMasked}
                  idDocumentMasked={idDocMasked}
                  contextRenderers={employeeContextRenderers}
                  renderField={renderField}
                  invalid={(field) => !!fe.get(field)}
                  error={(field) => fe.get(field)}
                  formData={layoutFormData}
                  effectiveSupervisor={employee?.effectiveSupervisor}
                />
              ),
              // „Pola dodatkowe" nie mają osobnej zakładki: pojedyncze pola układa w „Dane"
              // silnik formatek.
            }}
            contextRenderers={employeeContextRenderers}
          />
        )}

        {/* Modale */}
        <ConfirmDialog
          open={confirmDelete}
          title={t('common.confirmDelete')}
          message={fullName}
          variant="danger"
          onConfirm={() => {
            setConfirmDelete(false);
            deleteMutation.mutate();
          }}
          onClose={() => setConfirmDelete(false)}
        />

        {showAnonymize && employee && (
          <AnonymizeEmployeeModal
            name={`${employee.firstName} ${employee.lastName}`}
            loading={anonymizeMutation.isPending}
            onClose={() => setShowAnonymize(false)}
            onConfirm={() => anonymizeMutation.mutate()}
          />
        )}

        {showCreateAccount && employee && (
          <CreateAccountForExistingModal
            employeeId={employee.id}
            firstName={employee.firstName}
            lastName={employee.lastName}
            companyId={employee.companyId}
            defaultEmail={employee.workEmail}
            onClose={() => setShowCreateAccount(false)}
            onCreated={() => {
              setShowCreateAccount(false);
              toast.show({ variant: 'success', title: t('tenant.employees.toast.accountCreated') });
              if (id) queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_KEY(id) });
              queryClient.invalidateQueries({ queryKey: ['employees'] });
            }}
          />
        )}
      </div>
    </DocumentPreviewDockHost>
  );
}
